import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  MenuItem,
  Modal,
  Rating,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Lottie from "../../../../../components/animation/LottieAnimation";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { baseBackend } from "../../../../../api/base/baseUrl";
import {
  createBookingCancellation,
  getCancellationReasons,
} from "../../../../../api/cancellation/bookingCancellation";
import {
  decodeDataToken,
  getToken,
} from "../../../../../helpers/token/refreshToken";

const CarIcon = () => (
  <Box
    component="svg"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 640 640"
    sx={{
      width: 28,
      height: 28,
      fill: "#0d47a1",
      flexShrink: 0,
    }}
  >
    <path d="M147 170.7L117.2 256L240.1 256L240.1 160L162.2 160C155.4 160 149.3 164.3 147.1 170.7zM48.6 257.9L86.5 149.6C97.8 117.5 128.1 96 162.1 96L360 96C385.2 96 408.9 107.9 424 128L520.2 256.3C587.1 260.5 640 316.1 640 384L640 400C640 435.3 611.3 464 576 464L559.6 464C555.6 508.9 517.9 544 472 544C426.1 544 388.4 508.9 384.4 464L239.7 464C235.7 508.9 198 544 152.1 544C106.2 544 68.5 508.9 64.5 464L64.1 464C28.8 464 .1 435.3 .1 400L.1 320C.1 289.9 20.8 264.7 48.7 257.9zM440 256L372.8 166.4C369.8 162.4 365 160 360 160L288 160L288 256L440 256zM152 496C174.1 496 192 478.1 192 456C192 433.9 174.1 416 152 416C129.9 416 112 433.9 112 456C112 478.1 129.9 496 152 496zM512 456C512 433.9 494.1 416 472 416C449.9 416 432 433.9 432 456C432 478.1 449.9 496 472 496C494.1 496 512 478.1 512 456z" />
  </Box>
);

const RouteLabel = ({ children }) => (
  <Typography
    variant="body2"
    sx={{
      minWidth: 24,
      color: "text.primary",
      fontWeight: 800,
      textAlign: "center",
      flexShrink: 0,
      fontSize: 18,
    }}
  >
    {children}
  </Typography>
);

RouteLabel.propTypes = {
  children: PropTypes.node.isRequired,
};

const DetailRow = ({ label, value, noWrap = false }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: "text.secondary" }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 700, minWidth: 0 }}
      noWrap={noWrap}
    >
      {value || "-"}
    </Typography>
  </Box>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  noWrap: PropTypes.bool,
};

const DetailCol = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      minWidth: 0,
      borderRadius: "6px",
      bgcolor: "grey.50",
    }}
  >
    <Typography
      variant="caption"
      sx={{ color: "text.secondary", fontWeight: 700, flexShrink: 0 }}
    >
      {label}:
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 0 }} noWrap>
      {value || "-"}
    </Typography>
  </Box>
);

DetailCol.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const getSubRouteLabel = (subRoute) =>
  subRoute?.name ??
  subRoute?.address ??
  subRoute?.description ??
  subRoute?.originName ??
  "";

const sortSubRoutesById = (subRoutes) =>
  [...subRoutes].sort((firstRoute, secondRoute) => {
    const firstId = Number(firstRoute?.id);
    const secondId = Number(secondRoute?.id);

    if (Number.isNaN(firstId) && Number.isNaN(secondId)) return 0;
    if (Number.isNaN(firstId)) return 1;
    if (Number.isNaN(secondId)) return -1;
    return firstId - secondId;
  });

const SubRoutesList = ({ subRoutes }) => {
  const sortedSubRoutes = Array.isArray(subRoutes)
    ? sortSubRoutesById(subRoutes)
    : [];

  if (sortedSubRoutes.length === 0) return null;

  return (
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Paraderos
      </Typography>
      <Stack spacing={0.5}>
        {sortedSubRoutes.map((subRoute, index) => (
          <Typography
            key={subRoute?.id ?? `${getSubRouteLabel(subRoute)}-${index}`}
            variant="body2"
            sx={{ fontWeight: 700 }}
          >
            {index + 1}. {getSubRouteLabel(subRoute) || "-"}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
};

SubRoutesList.propTypes = {
  subRoutes: PropTypes.array,
};

const OfferCard = ({ offer, onAccept, onReject, disabled }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: "8px",
      border: "1px solid #000",
      bgcolor: "background.paper",
    }}
  >
    <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
      <Stack spacing={1}>
        <DetailCol label="Conductor" value={offer.nameDriver} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1,
          }}
        >
          <DetailCol label="Precio" value={offer.priceOffer} />
          <DetailCol label="Tiempo" value={offer.timeArrived} />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
            gap: 1,
          }}
        >
          <DetailCol label="Placa" value={offer.plate} />
          <DetailCol label="Marca" value={offer.brand} />
          <DetailCol label="Color" value={offer.color} />
        </Box>

        <Typography variant="caption">
          Vence en {offer.secondsLeft} s
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            disabled={disabled}
            color="error"
            onClick={() => onReject(offer)}
          >
            Rechazar
          </Button>
          <Button
            disabled={disabled}
            size="small"
            color="primary"
            variant="contained"
            onClick={(event) => {
              event.preventDefault();
              onAccept(offer);
            }}
          >
            Aceptar
          </Button>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

OfferCard.propTypes = {
  offer: PropTypes.object.isRequired,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const OffersModal = ({
  open,
  onClose,
  modalStyle,
  bookingNow,
  driverSelect,
  cancelBooking,
  offersList,
  acceptCounterOffer,
  rejectCounterOffer,
  offersLoading,
  offersError,
  offersBusy,
  waitingProjection,
  offersState,
  onRefreshOffers,
  onBack,
  supportPhone,
}) => {
  const [openRatingModal, setOpenRatingModal] = useState(false);
  const [driverRating, setDriverRating] = useState(0);
  const [driverComment, setDriverComment] = useState("");
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [isLoadingRating, setIsLoadingRating] = useState(false);
  const [existingDriverCommentId, setExistingDriverCommentId] = useState(null);
  const [ratingError, setRatingError] = useState("");
  const [openCancellationModal, setOpenCancellationModal] = useState(false);
  const [cancellationReasons, setCancellationReasons] = useState([]);
  const [selectedCancellationReasonId, setSelectedCancellationReasonId] =
    useState("");
  const [isLoadingCancellationReasons, setIsLoadingCancellationReasons] =
    useState(false);
  const [isSubmittingCancellation, setIsSubmittingCancellation] =
    useState(false);
  const [cancellationError, setCancellationError] = useState("");
  const autoOpenedRatingBookingRef = useRef(null);
  const driverOffers = Array.isArray(offersList)
    ? [...offersList].sort(
        (firstOffer, secondOffer) =>
          firstOffer.pickupEtaDurationSeconds -
          secondOffer.pickupEtaDurationSeconds,
      )
    : [];
  const hasDriver = Boolean(driverSelect);
  const isTripRunning = bookingNow?.statusService === "RUNNING";
  const isTripFinished = bookingNow?.statusService === "FINISH";
  const canRateDriver = isTripRunning || isTripFinished;
  const isBookingPending =
    bookingNow?.statusBooking === "PENDING" ||
    bookingNow?.statusBooking === "PENDIENTE";
  const isBookingRunning = bookingNow?.statusBooking === "RUNNING";
  const bookingStatusLabel = isTripFinished
    ? "FINALIZADO"
    : isTripRunning
      ? "EN VIAJE"
      : "ESPERANDO...";
  const bookingStatusColor = isTripFinished
    ? {
        bgcolor: "#e8f5e9",
        color: "#1b5e20",
      }
    : isTripRunning
      ? {
          bgcolor: "#e3f2fd",
          color: "#0d47a1",
        }
      : {
          bgcolor: "#fff7e0",
          color: "#8a5a00",
        };
  const whatsappUrl = `https://api.whatsapp.com/send?phone=+51${supportPhone}&text=*Pedido:* ${bookingNow?.counterBooking},%0A *Cliente:* ${bookingNow?.clientName},%0A *Origen:* ${bookingNow?.origin?.name}, *Destino:* ${bookingNow?.destination?.name} `;
  const handleOpenRatingModal = useCallback(async () => {
    setOpenRatingModal(true);
    setRatingError("");
    setExistingDriverCommentId(null);
    setDriverRating(0);
    setDriverComment("");

    if (!bookingNow?.counterBooking) return;

    setIsLoadingRating(true);
    try {
      const response = await baseBackend.get(
        `/api/v2/comments/driver/booking/${bookingNow.counterBooking}`,
        {
          headers: { "Content-Type": "application/json" },
        },
      );
      const currentComment = Array.isArray(response?.data?.data)
        ? response.data.data[0]
        : null;

      if (currentComment) {
        setExistingDriverCommentId(currentComment.id);
        setDriverRating(Number(currentComment.rating) || 0);
        setDriverComment(currentComment.comments || "");
      }
    } catch (error) {
      console.log(error);
      setRatingError("No se pudo consultar la calificacion anterior.");
    } finally {
      setIsLoadingRating(false);
    }
  }, [bookingNow?.counterBooking]);
  const handleCloseRatingModal = () => {
    setOpenRatingModal(false);
    setDriverRating(0);
    setDriverComment("");
    setIsSubmittingRating(false);
    setIsLoadingRating(false);
    setExistingDriverCommentId(null);
    setRatingError("");
  };
  const handleSubmitRating = async (event) => {
    event.preventDefault();

    const driverId = bookingNow?.idDriver ?? driverSelect?.idDriver;
    if (!driverId || !bookingNow?.counterBooking || driverRating === 0) return;

    const ratingPayload = {
      drivers_id: Number(driverId),
      booking_type: "ENTERPRISE",
      booking_id: bookingNow.counterBooking,
      name: bookingNow?.clientName || "",
      rating: driverRating,
      comments: driverComment,
    };

    setIsSubmittingRating(true);
    setRatingError("");

    try {
      if (existingDriverCommentId) {
        await baseBackend.put(
          "/api/v2/comments/driver",
          JSON.stringify({
            id: existingDriverCommentId,
            ...ratingPayload,
          }),
          {
            headers: { "Content-Type": "application/json" },
          },
        );
      } else {
        await baseBackend.post(
          "/api/v2/comments/driver",
          JSON.stringify(ratingPayload),
          {
            headers: { "Content-Type": "application/json" },
          },
        );
      }
      handleCloseRatingModal();
    } catch (error) {
      console.log(error);
      setRatingError("No se pudo enviar la calificacion.");
      setIsSubmittingRating(false);
    }
  };
  const handleOpenCancellationModal = async () => {
    setOpenCancellationModal(true);
    setCancellationError("");
    setSelectedCancellationReasonId("");

    if (cancellationReasons.length > 0) return;

    setIsLoadingCancellationReasons(true);
    try {
      const response = await getCancellationReasons();
      const reasons = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      const enterpriseReasons = reasons.filter(
        (reason) =>
          reason?.active !== false &&
          (!reason?.actor_type || reason.actor_type === "ENTERPRISE"),
      );

      setCancellationReasons(
        enterpriseReasons.length > 0
          ? enterpriseReasons
          : reasons.filter((reason) => reason?.active !== false),
      );
    } catch (error) {
      console.log(error);
      setCancellationError("No se pudieron cargar los motivos de cancelacion.");
    } finally {
      setIsLoadingCancellationReasons(false);
    }
  };
  const handleCloseCancellationModal = () => {
    if (isSubmittingCancellation) return;

    setOpenCancellationModal(false);
    setSelectedCancellationReasonId("");
    setCancellationError("");
  };
  const getEnterpriseId = () => {
    if (bookingNow?.enterpriseId) {
      const enterpriseId = Number(bookingNow.enterpriseId);
      return Number.isNaN(enterpriseId) ? null : enterpriseId;
    }

    try {
      const dataToken = getToken("dttkn");
      if (!dataToken) return null;
      const enterpriseId = Number(decodeDataToken(dataToken).enterprise_id);
      return Number.isNaN(enterpriseId) ? null : enterpriseId;
    } catch (error) {
      console.log(error);
      return null;
    }
  };
  const handleConfirmCancellation = async (event) => {
    event.preventDefault();

    if (!selectedCancellationReasonId) {
      setCancellationError("Selecciona un motivo de cancelacion.");
      return;
    }

    if (!bookingNow?.counterBooking || !bookingNow?.uuid) {
      setCancellationError("No se encontro la informacion del pedido.");
      return;
    }

    setIsSubmittingCancellation(true);
    setCancellationError("");

    try {
      await createBookingCancellation({
        booking_id: Number(bookingNow.counterBooking),
        cancelled_by_type: "ENTERPRISE",
        cancelled_by_id: getEnterpriseId(),
        cancellation_reason_id: Number(selectedCancellationReasonId),
        booking_status_before: bookingNow?.statusBooking ?? null,
        cancellation_fee: 0,
      });

      await cancelBooking(bookingNow.uuid);
      setOpenCancellationModal(false);
      setSelectedCancellationReasonId("");
      setIsSubmittingCancellation(false);
    } catch (error) {
      console.log(error);
      setCancellationError("No se pudo confirmar la cancelacion.");
      setIsSubmittingCancellation(false);
    }
  };

  useEffect(() => {
    const bookingKey = bookingNow?.counterBooking ?? bookingNow?.uuid;

    if (!open || !hasDriver || !isTripFinished || !bookingKey) {
      return;
    }

    if (autoOpenedRatingBookingRef.current === bookingKey) {
      return;
    }

    autoOpenedRatingBookingRef.current = bookingKey;
    handleOpenRatingModal();
  }, [
    bookingNow?.counterBooking,
    bookingNow?.uuid,
    hasDriver,
    handleOpenRatingModal,
    isTripFinished,
    open,
  ]);

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          style={{
            ...modalStyle,
            background: "white",
          }}
          sx={{
            width: {
              xs: "95%",
              sm: "600px",
            },
            minHeight: { xs: "30vh" },
            maxHeight: { xs: "90vh", sm: "95vh" },
            overflowY: "auto",
            outline: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1,
              py: 0.5,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              <Button onClick={onBack} variant="contained" color="primary">
                Volver{" "}
              </Button>{" "}
            </Typography>
            <IconButton size="small" color="error" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {offersError && (
            <Alert
              severity="error"
              action={<Button onClick={onRefreshOffers}>Actualizar</Button>}
            >
              {offersError}
            </Alert>
          )}
          {offersLoading && <Alert severity="info">Cargando ofertas...</Alert>}
          {offersBusy && (
            <Alert severity="info">Verificando operación...</Alert>
          )}
          {waitingProjection && (
            <Alert severity="info">
              Esperando confirmación de la asignación en el pedido...
            </Alert>
          )}
          {offersState === "CLOSED" && (
            <Alert severity="info">Este pedido está cerrado.</Alert>
          )}
          <Card
            variant="outlined"
            sx={{
              borderRadius: "10px",
              border: "1px solid #000",
              m: 0.5,
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Stack spacing={1}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1,
                  }}
                >
                  <DetailCol
                    label="Pedido"
                    value={bookingNow?.counterBooking}
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 1,
                    }}
                  >
                    <DetailCol label="Fecha" value={bookingNow?.date} />
                    <DetailCol label="Hora" value={bookingNow?.time} />
                  </Box>
                </Box>

                <Divider />

                {bookingNow?.otherPersonName && (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1,
                      }}
                    >
                      <DetailCol
                        label="Nombre"
                        value={bookingNow?.otherPersonName}
                      />
                      <DetailCol
                        label="Tel"
                        value={bookingNow?.otherPersonPhone}
                      />
                    </Box>
                    <Divider />
                  </>
                )}

                <DetailRow
                  label="Origen"
                  value={bookingNow?.origin?.name}
                  noWrap
                />
                <SubRoutesList subRoutes={bookingNow?.subRoutes} />
                <DetailRow
                  label="Destino"
                  value={bookingNow?.destination?.name}
                  noWrap
                />

                {hasDriver ? (
                  <>
                    <Divider />
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 800, color: "#0d7400" }}
                      >
                        Pedido Aceptado
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          px: 1,
                          py: 0.35,
                          borderRadius: "6px",
                          bgcolor: bookingStatusColor.bgcolor,
                          color: bookingStatusColor.color,
                          fontWeight: 800,
                          flexShrink: 0,
                        }}
                      >
                        {bookingStatusLabel}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1,
                      }}
                    >
                      <DetailCol
                        label="Conductor"
                        value={driverSelect?.driverName}
                      />
                      <DetailCol
                        label="Llegara en"
                        value={driverSelect?.time}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                        gap: 1,
                      }}
                    >
                      <DetailCol label="Placa" value={driverSelect?.plate} />
                      <DetailCol label="Marca" value={driverSelect?.bodywork} />
                      <DetailCol label="Color" value={driverSelect?.color} />
                    </Box>
                    <DetailCol label="Tel" value={driverSelect?.driverPhone} />
                    {canRateDriver && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          onClick={(event) => {
                            event.preventDefault();
                            handleOpenRatingModal();
                          }}
                        >
                          Calificar Conductor
                        </Button>
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      {isBookingPending ? (
                        <CarIcon />
                      ) : (
                        <RouteLabel>A</RouteLabel>
                      )}
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          overflow: "hidden",
                          "@keyframes moveCarLottie": {
                            "0%": {
                              transform: "translateX(-120px)",
                            },
                            "100%": {
                              transform: "translateX(calc(100% + 120px))",
                            },
                          },
                        }}
                      >
                        <Box
                          sx={{
                            animation: "moveCarLottie 8s linear infinite",
                          }}
                        >
                          {/* <Lottie
                            src="/assets/carmove.json"
                            loop
                            autoplay
                            style={{
                              width: 100,
                              height: 50,
                            }}
                          /> */}
                          <CarIcon />
                        </Box>
                        <div
                          style={{
                            position: "absolute",
                            bottom: 12,
                            left: 0,
                            width: "100%",
                            background: "#000",
                            height: "2px",
                          }}
                        ></div>
                      </Box>
                      <RouteLabel>{isBookingRunning ? "B" : "A"}</RouteLabel>
                    </Box>
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      size="small"
                      color="error"
                      variant="contained"
                      disabled={
                        offersBusy ||
                        Number(bookingNow?.idDriver) > 0 ||
                        ["ASSIGNED", "CLOSED"].includes(offersState)
                      }
                      onClick={(event) => {
                        event.preventDefault();
                        handleOpenCancellationModal();
                      }}
                    >
                      Cancelar
                    </Button>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
          <div style={{ padding: "8px" }}>
            <Button
              size="small"
              color="success"
              variant="contained"
              startIcon={<WhatsAppIcon />}
              sx={{
                width: "100%",
                height: "45px",
                fontWeight: 800,
                background: "#128C7E",
              }}
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
            >
              Consultar al {supportPhone}
            </Button>
          </div>
          {!hasDriver && !waitingProjection && offersState !== "CLOSED" && (
            <>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, textAlign: "center", mt: 2, mb: 1 }}
              >
                Buscando conductores cercanos
              </Typography>

              {driverOffers.length > 0 ? (
                <Stack spacing={1} sx={{ p: 0.5 }}>
                  {driverOffers.map((offer) => (
                    <OfferCard
                      key={offer.offerId}
                      offer={offer}
                      onAccept={acceptCounterOffer}
                      onReject={rejectCounterOffer}
                      disabled={
                        offersBusy || offersLoading || Boolean(offersError)
                      }
                    />
                  ))}
                </Stack>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    pb: 1,
                  }}
                >
                  <Lottie
                    src="/assets/radar.json"
                    loop
                    autoplay
                    style={{
                      width: 120,
                      height: 120,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ textAlign: "center", mt: 1 }}
                  >
                    {offersLoading
                      ? "Consultando ofertas..."
                      : offersError
                        ? "No se pudieron actualizar las ofertas"
                        : "Esperando nuevas ofertas"}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Modal>

      <Modal open={openRatingModal} onClose={handleCloseRatingModal}>
        <Box
          component="form"
          onSubmit={handleSubmitRating}
          style={{
            ...modalStyle,
            height: "50vh",
            background: "white",
          }}
          sx={{
            width: { xs: "90%", sm: "420px" },

            outline: "none",
          }}
        >
          <Card variant="outlined" sx={{ borderRadius: "8px" }}>
            <CardContent>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Calificar Conductor
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={handleCloseRatingModal}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", mb: 1 }}
                  >
                    {isLoadingRating
                      ? "Consultando calificacion anterior..."
                      : "Selecciona una calificacion"}
                  </Typography>
                  <Rating
                    size="large"
                    precision={0.5}
                    disabled={isLoadingRating || isSubmittingRating}
                    value={driverRating}
                    onChange={(_, newValue) => {
                      setDriverRating(newValue || 0);
                    }}
                  />
                </Box>

                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Comentario"
                  value={driverComment}
                  disabled={isLoadingRating || isSubmittingRating}
                  onChange={(event) => setDriverComment(event.target.value)}
                />

                {ratingError && (
                  <Typography variant="body2" sx={{ color: "error.main" }}>
                    {ratingError}
                  </Typography>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={isSubmittingRating}
                    onClick={handleCloseRatingModal}
                  >
                    Cerrar
                  </Button>
                  <Button
                    size="small"
                    type="submit"
                    variant="contained"
                    disabled={
                      driverRating === 0 ||
                      isSubmittingRating ||
                      isLoadingRating
                    }
                  >
                    {isSubmittingRating
                      ? "Enviando..."
                      : existingDriverCommentId
                        ? "Actualizar"
                        : "Enviar"}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Modal>

      <Modal
        open={openCancellationModal}
        onClose={handleCloseCancellationModal}
      >
        <Box
          component="form"
          onSubmit={handleConfirmCancellation}
          style={{
            ...modalStyle,
            background: "white",
          }}
          sx={{
            width: { xs: "90%", sm: "430px" },
            outline: "none",
          }}
        >
          <Card variant="outlined" sx={{ borderRadius: "8px" }}>
            <CardContent>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Cancelar pedido
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={isSubmittingCancellation}
                    onClick={handleCloseCancellationModal}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Selecciona el motivo para confirmar la cancelacion del pedido
                  TM-{bookingNow?.counterBooking}.
                </Typography>

                <TextField
                  select
                  fullWidth
                  label="Motivo de cancelacion"
                  value={selectedCancellationReasonId}
                  disabled={
                    isLoadingCancellationReasons || isSubmittingCancellation
                  }
                  onChange={(event) => {
                    setSelectedCancellationReasonId(event.target.value);
                    setCancellationError("");
                  }}
                >
                  <MenuItem value="" disabled>
                    Selecciona un motivo
                  </MenuItem>
                  {cancellationReasons.map((reason) => (
                    <MenuItem key={reason.id} value={reason.id}>
                      {reason.name}
                    </MenuItem>
                  ))}
                </TextField>

                {isLoadingCancellationReasons && (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Cargando motivos...
                  </Typography>
                )}

                {cancellationError && (
                  <Typography variant="body2" sx={{ color: "error.main" }}>
                    {cancellationError}
                  </Typography>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={isSubmittingCancellation}
                    onClick={handleCloseCancellationModal}
                  >
                    Cerrar
                  </Button>
                  <Button
                    size="small"
                    type="submit"
                    color="error"
                    variant="contained"
                    disabled={
                      !selectedCancellationReasonId ||
                      isSubmittingCancellation ||
                      isLoadingCancellationReasons
                    }
                  >
                    {isSubmittingCancellation
                      ? "Cancelando..."
                      : "Confirmar cancelacion"}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Modal>
    </>
  );
};

OffersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  modalStyle: PropTypes.object.isRequired,
  bookingNow: PropTypes.object,
  driverSelect: PropTypes.object,
  cancelBooking: PropTypes.func.isRequired,
  offersList: PropTypes.array.isRequired,
  acceptCounterOffer: PropTypes.func.isRequired,
  rejectCounterOffer: PropTypes.func.isRequired,
  offersLoading: PropTypes.bool,
  offersError: PropTypes.string,
  offersBusy: PropTypes.bool,
  waitingProjection: PropTypes.bool,
  offersState: PropTypes.string,
  onRefreshOffers: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  loaderClassName: PropTypes.string.isRequired,
  supportPhone: PropTypes.string.isRequired,
};

export default OffersModal;
